import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Card, Empty, Grid, List, Select, Spin, Space, Typography, Button, Tag, Modal, Input, Divider, Row } from 'antd';
import { CloseOutlined, PlusOutlined, EditOutlined, DeleteOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import { JudgeType } from '@vote4dance/judging-schema';
import { apis, hooks, models } from '@vote4dance/judging-sdk';
import { _T } from '../../../../../base-react/src/Localization';
import { SortableList, SortableListItem } from '../../../../components/SortableList';
import { Link } from 'react-router-dom';
import { ApplicationStatusDot, getActiveApplicationUserIds } from '../../../../components/ApplicationStatusDot';
var Stream = hooks.streams.Stream;
var ClassItem = models.classes.ClassItem;
var ClassesSource = models.classes.ClassesSource;
var RoundItem = models.rounds.RoundItem;
var CompetitionRoundSource = models.rounds.CompetitionRoundSource;
var JudgeItem = models.judges.JudgeItem;
var JudgeSource = models.judges.JudgeSource;
var JudgingPanelSource = models.judges.JudgingPanelSource;
var PanelJudgeSource = models.judges.PanelJudgeSource;
var Api = apis.Api;
const { Text } = Typography;
const { useBreakpoint } = Grid;
const PanelJudgesList = ({ panel, judges, competition_id, disabled, loadingJudges, activeJudgeUserIds }) => {
    var _a;
    const { notification } = App.useApp();
    const panelId = panel.panel_id;
    const panelLocked = Number((_a = panel.panel_locked) !== null && _a !== void 0 ? _a : 0) > 0;
    const panelJudgeSource = useMemo(() => (panelId ? PanelJudgeSource(competition_id, panelId) : undefined), [competition_id, panelId]);
    const [panelJudges, loadingPanelJudges] = Stream(panelJudgeSource);
    const [selectedJudge, setSelectedJudge] = useState();
    const [assigningJudge, setAssigningJudge] = useState();
    const [removingJudge, setRemovingJudge] = useState();
    const [changingJudgeType, setChangingJudgeType] = useState({});
    const assignedIds = panelJudges === null || panelJudges === void 0 ? void 0 : panelJudges.map(pj => pj.judge_id);
    const availableJudges = judges === null || judges === void 0 ? void 0 : judges.filter(judge => judge.judge_id && !(assignedIds === null || assignedIds === void 0 ? void 0 : assignedIds.includes(judge.judge_id)));
    const judgeTypes = [JudgeType.Normal, JudgeType.Trainee, JudgeType.ChiefIn, JudgeType.ChiefOut];
    const judgeTypeOptions = judgeTypes.map(type => ({
        label: _T(`judges.type.${type}.title`),
        value: type
    }));
    const handleAddJudge = async (judge_id) => {
        var _a, _b, _c;
        if (!panelId || panelLocked) {
            return;
        }
        const judge = availableJudges === null || availableJudges === void 0 ? void 0 : availableJudges.find(j => j.judge_id === judge_id);
        if (!judge) {
            return;
        }
        const panelJudgeUrl = panelJudgeSource === null || panelJudgeSource === void 0 ? void 0 : panelJudgeSource.url;
        if (!panelJudgeUrl) {
            return;
        }
        setAssigningJudge(judge_id);
        try {
            await Api.create(panelJudgeUrl, {
                ...judge,
                judge_type: JudgeType.Normal
            });
            setSelectedJudge(undefined);
        }
        catch (error) {
            notification.error({ message: _T((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : 'common.error') });
        }
        finally {
            setAssigningJudge(undefined);
        }
    };
    const handleRemoveJudge = async (judge) => {
        var _a, _b, _c;
        if (!panelId || panelLocked) {
            return;
        }
        const panelJudgeUrl = panelJudgeSource === null || panelJudgeSource === void 0 ? void 0 : panelJudgeSource.url;
        if (!panelJudgeUrl) {
            return;
        }
        setRemovingJudge(judge.judge_id);
        try {
            await Api.destroy(`${panelJudgeUrl}/${judge.judge_id}`);
        }
        catch (error) {
            notification.error({ message: _T((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : 'common.error') });
        }
        finally {
            setRemovingJudge(undefined);
        }
    };
    const handleChangeJudgeType = async (judge, type) => {
        var _a, _b, _c;
        if (!panelId || panelLocked) {
            return;
        }
        const panelJudgeUrl = panelJudgeSource === null || panelJudgeSource === void 0 ? void 0 : panelJudgeSource.url;
        if (!panelJudgeUrl) {
            return;
        }
        setChangingJudgeType(prev => ({ ...prev, [judge.judge_id]: true }));
        try {
            await Api.update(`${panelJudgeUrl}/${judge.judge_id}`, {
                ...judge,
                judge_type: type
            });
        }
        catch (error) {
            notification.error({ message: _T((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : 'common.error') });
        }
        finally {
            setChangingJudgeType(prev => {
                const next = { ...prev };
                delete next[judge.judge_id];
                return next;
            });
        }
    };
    const renderPanelJudges = () => {
        if (loadingPanelJudges) {
            return React.createElement(Spin, null);
        }
        return (React.createElement(List, { dataSource: panelJudges, renderItem: pj => {
                const judge = judges === null || judges === void 0 ? void 0 : judges.find(j => j.judge_id === pj.judge_id);
                const label = judge ? `${judge.judge_letter ? `${judge.judge_letter}) ` : ''}${JudgeItem.getFullName(judge)}` : pj.judge_id;
                return (React.createElement(List.Item, { key: pj.judge_id, actions: [
                        React.createElement(Select, { key: "type-select", value: pj.judge_type, options: judgeTypeOptions, onChange: (value) => handleChangeJudgeType(pj, value), disabled: disabled || panelLocked, loading: !!changingJudgeType[pj.judge_id], style: { minWidth: 160 } }),
                        React.createElement(Button, { key: "remove", type: "text", danger: true, icon: React.createElement(CloseOutlined, null), onClick: () => handleRemoveJudge(pj), disabled: disabled || panelLocked || removingJudge === pj.judge_id, loading: removingJudge === pj.judge_id })
                    ] },
                    React.createElement(Space, { size: "small" },
                        React.createElement(ApplicationStatusDot, { connected: activeJudgeUserIds.has(String(pj.user_id)), applicationName: "judging", applicationUserId: pj.user_id }),
                        React.createElement(Typography.Text, { strong: true }, label))));
            } }));
    };
    return (React.createElement(Space, { orientation: "vertical", size: "small", style: { width: '100%' } },
        renderPanelJudges(),
        React.createElement(Select, { value: selectedJudge, placeholder: _T('common.add', { name: _T('judges.title').toLowerCase() }), options: availableJudges === null || availableJudges === void 0 ? void 0 : availableJudges.map(judge => ({
                label: `${judge.judge_letter ? `${judge.judge_letter}) ` : ''}${JudgeItem.getFullName(judge)}`,
                value: judge.judge_id
            })), showSearch: true, optionFilterProp: "label", filterOption: (input, option) => (option === null || option === void 0 ? void 0 : option.label).toLowerCase().includes(input.toLowerCase()), disabled: disabled || panelLocked || loadingJudges || !availableJudges || availableJudges.length === 0, loading: !!assigningJudge, onSelect: (value) => handleAddJudge(value), onChange: (value) => setSelectedJudge(value), allowClear: true, style: { minWidth: 180 } })));
};
export const JudgingPanelsOverview = ({ event_id, competition_id, disabled }) => {
    const [classes, loadingClasses] = Stream(ClassesSource(competition_id));
    const panelSource = useMemo(() => JudgingPanelSource(competition_id), [competition_id]);
    const [panels, loadingPanels] = Stream(panelSource);
    const [rounds, loadingRounds] = Stream(CompetitionRoundSource(competition_id));
    const [judges, loadingJudges] = Stream(JudgeSource(competition_id));
    const [applications] = Stream(models.applications.CompetitionApplicationsSource(event_id));
    const { modal, notification } = App.useApp();
    const [panelModalOpen, setPanelModalOpen] = useState(false);
    const [editingPanel, setEditingPanel] = useState(null);
    const [panelName, setPanelName] = useState('');
    const panelNameInputRef = useRef(null);
    const screens = useBreakpoint();
    const isXs = !!screens.xs;
    const [savingPanel, setSavingPanel] = useState(false);
    const [deletingPanelId, setDeletingPanelId] = useState();
    const [sortedPanels, setSortedPanels] = useState([]);
    const [expandedPanels, setExpandedPanels] = useState({});
    const isPanelLocked = (panel) => { var _a; return Number((_a = panel.panel_locked) !== null && _a !== void 0 ? _a : 0) > 0; };
    const activeJudgeUserIds = useMemo(() => getActiveApplicationUserIds(applications, 'judging'), [applications]);
    const classLookup = useMemo(() => {
        const lookup = new Map();
        classes === null || classes === void 0 ? void 0 : classes.forEach(cls => {
            if (cls.class_id) {
                lookup.set(cls.class_id, cls);
            }
        });
        return lookup;
    }, [classes]);
    useEffect(() => {
        setSortedPanels(panels !== null && panels !== void 0 ? panels : []);
    }, [panels]);
    useEffect(() => {
        if (panelModalOpen) {
            setTimeout(() => { var _a; return (_a = panelNameInputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
        }
    }, [panelModalOpen]);
    const panelDetails = useMemo(() => {
        if (!sortedPanels) {
            return [];
        }
        return sortedPanels.map(panel => {
            const assignedClasses = (classes || []).filter(cls => cls.default_panel_id === panel.panel_id);
            const panelRounds = (rounds || []).filter(round => round.panel_id === panel.panel_id);
            const overrideRounds = panelRounds;
            return { panel_id: panel.panel_id, panel, assignedClasses, overrideRounds };
        });
    }, [sortedPanels, classes, rounds, classLookup]);
    const isLoading = loadingClasses || loadingPanels || loadingRounds || loadingJudges || panels === undefined;
    const openCreatePanel = () => {
        var _a;
        setEditingPanel(null);
        setPanelName(((_a = panels === null || panels === void 0 ? void 0 : panels.length) !== null && _a !== void 0 ? _a : 0) === 0 ? 'Panel A' : '');
        setPanelModalOpen(true);
    };
    const openEditPanel = (panel) => {
        if (isPanelLocked(panel)) {
            return;
        }
        setEditingPanel(panel);
        setPanelName(panel.panel_name || '');
        setPanelModalOpen(true);
    };
    const handleSavePanel = async () => {
        var _a, _b, _c;
        const name = panelName.trim();
        if (!name) {
            notification.error({ message: _T('common.required') });
            return;
        }
        const panelUrl = panelSource === null || panelSource === void 0 ? void 0 : panelSource.url;
        if (!panelUrl) {
            return;
        }
        setSavingPanel(true);
        try {
            if (editingPanel === null || editingPanel === void 0 ? void 0 : editingPanel.panel_id) {
                await Api.update(`${panelUrl}/${editingPanel.panel_id}`, {
                    ...editingPanel,
                    panel_name: name
                });
            }
            else {
                await Api.create(panelUrl, {
                    competition_id,
                    panel_name: name
                });
            }
            setPanelModalOpen(false);
        }
        catch (error) {
            notification.error({ message: _T((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : 'common.error') });
        }
        finally {
            setSavingPanel(false);
        }
    };
    const handleDeletePanel = (panel) => {
        const panelUrl = panelSource === null || panelSource === void 0 ? void 0 : panelSource.url;
        if (!panel.panel_id || !panelUrl) {
            return;
        }
        modal.confirm({
            title: _T('common.delete.title', { name: panel.panel_name }),
            content: _T('common.delete.content', { name: panel.panel_name }),
            okButtonProps: { danger: true },
            onOk: async () => {
                var _a, _b, _c;
                setDeletingPanelId(panel.panel_id);
                try {
                    await Api.destroy(`${panelUrl}/${panel.panel_id}`);
                    setPanelModalOpen(false);
                }
                catch (error) {
                    notification.error({ message: _T((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : 'common.error') });
                }
                finally {
                    setDeletingPanelId(undefined);
                }
            }
        });
    };
    const handlePanelSorted = (newList) => {
        if (disabled || !(panelSource === null || panelSource === void 0 ? void 0 : panelSource.url)) {
            return;
        }
        setSortedPanels(newList);
        Api.call(panelSource.url, 'set-order', newList.map(panel => panel.panel_id))
            .catch((error) => {
            var _a, _b, _c;
            setSortedPanels(panels !== null && panels !== void 0 ? panels : []);
            notification.error({ message: _T((_c = (_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : error === null || error === void 0 ? void 0 : error.message) !== null && _c !== void 0 ? _c : 'common.error') });
        });
    };
    const panelModalFooter = [
        editingPanel ? (React.createElement(Button, { key: "delete", danger: true, loading: deletingPanelId === editingPanel.panel_id, onClick: () => handleDeletePanel(editingPanel), disabled: disabled },
            React.createElement(DeleteOutlined, null),
            _T('common.delete.title'))) : null,
        React.createElement(Button, { key: "cancel", onClick: () => setPanelModalOpen(false) }, _T('common.cancel.title')),
        React.createElement(Button, { key: "save", type: "primary", onClick: handleSavePanel, loading: savingPanel, disabled: disabled }, _T('common.save'))
    ].filter(Boolean);
    return (React.createElement(Card, { size: "small", title: _T('panel.title') },
        isLoading ? (React.createElement(Space, { style: { width: '100%', justifyContent: 'center' } },
            React.createElement(Spin, null))) : panels && panels.length > 0 ? (React.createElement(SortableList, { contextId: competition_id.toString(), items: panelDetails, idAttribute: "panel_id", disabled: disabled, onSorted: (newList) => handlePanelSorted(newList.map(item => item.panel)), renderItem: (detail) => (React.createElement(SortableListItem, { item: detail, idAttribute: "panel_id", disabled: disabled || isPanelLocked(detail.panel), flipped: true, element: () => {
                    var _a;
                    const isExpanded = !!expandedPanels[(_a = detail.panel_id) !== null && _a !== void 0 ? _a : ''];
                    return (React.createElement("div", null,
                        React.createElement(Row, { onClick: () => setExpandedPanels(prev => {
                                var _a;
                                return ({
                                    ...prev,
                                    [(_a = detail.panel_id) !== null && _a !== void 0 ? _a : '']: !isExpanded
                                });
                            }), style: { padding: isXs ? 4 : 8 } },
                            React.createElement(Space, { size: isXs ? 4 : 'small' },
                                isExpanded ? React.createElement(DownOutlined, null) : React.createElement(RightOutlined, null),
                                React.createElement(Button, { type: "text", size: isXs ? 'small' : 'middle', onClick: (event) => {
                                        event.stopPropagation();
                                        setExpandedPanels(prev => {
                                            var _a;
                                            return ({
                                                ...prev,
                                                [(_a = detail.panel_id) !== null && _a !== void 0 ? _a : '']: !isExpanded
                                            });
                                        });
                                    } },
                                    React.createElement(Space, null,
                                        React.createElement(Text, { strong: true }, detail.panel.panel_name),
                                        React.createElement(React.Fragment, null,
                                            React.createElement(Tag, { color: "processing" },
                                                detail.panel.judge_count,
                                                " ",
                                                !screens.xs && _T('judges.plural').toLowerCase()),
                                            React.createElement(Tag, { color: "default" },
                                                detail.assignedClasses.length,
                                                " ",
                                                !screens.xs && _T('class.plural').toLowerCase()),
                                            detail.overrideRounds.length > 0 && (React.createElement(Tag, { color: "warning" },
                                                detail.overrideRounds.length,
                                                " ",
                                                !screens.xs && _T('round.plural').toLowerCase()))))),
                                !disabled && (React.createElement(Button, { type: "text", size: isXs ? 'small' : 'middle', icon: React.createElement(EditOutlined, null), disabled: isPanelLocked(detail.panel), onClick: (event) => {
                                        event.stopPropagation();
                                        openEditPanel(detail.panel);
                                    } })))),
                        isExpanded && (React.createElement(Space, { orientation: "vertical", size: "small", style: { width: '100%' } },
                            React.createElement(PanelJudgesList, { panel: detail.panel, judges: judges, competition_id: competition_id, disabled: disabled, loadingJudges: loadingJudges, activeJudgeUserIds: activeJudgeUserIds }),
                            React.createElement(Divider, null),
                            React.createElement("div", null,
                                React.createElement(Text, { strong: true }, _T('class.plural')),
                                React.createElement(Space, { wrap: true, size: "small", style: { marginLeft: 4 } }, !screens.xs && detail.assignedClasses.length ? (detail.assignedClasses.map(cls => (React.createElement(Tag, { key: cls.class_id }, ClassItem.getClassLabel(cls))))) : detail.assignedClasses.length === 0 ? (React.createElement(Text, { type: "secondary" }, _T('common.none'))) : null)),
                            detail.overrideRounds.length > 0 &&
                                React.createElement("div", null,
                                    React.createElement(Text, { strong: true }, _T('round.plural')),
                                    React.createElement(Space, { wrap: true, size: "small", style: { marginLeft: 4 } }, !screens.xs ? (detail.overrideRounds.map(round => {
                                        const classLabel = classLookup.get(round.class_id)
                                            ? ClassItem.getClassLabel(classLookup.get(round.class_id))
                                            : '';
                                        return (React.createElement(Link, { key: round.round_id, to: `../classes/${round.class_id}/${round.round_id}` },
                                            React.createElement(Tag, null,
                                                RoundItem.getRoundLabel(round),
                                                classLabel ? ` (${classLabel})` : '')));
                                    })) : null))))));
                } })) })) : (React.createElement(Empty, { image: Empty.PRESENTED_IMAGE_SIMPLE })),
        React.createElement(Modal, { title: editingPanel
                ? _T('common.edit')
                : _T('common.create', { name: _T('panel.title') }), open: panelModalOpen, afterOpenChange: (open) => {
                if (open) {
                    setTimeout(() => { var _a; return (_a = panelNameInputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
                }
            }, onOk: handleSavePanel, onCancel: () => setPanelModalOpen(false), okText: _T('common.save'), confirmLoading: savingPanel, okButtonProps: { disabled: disabled }, cancelText: _T('common.cancel.title'), footer: panelModalFooter },
            React.createElement(Input, { ref: panelNameInputRef, value: panelName, onChange: (event) => setPanelName(event.target.value), onPressEnter: (event) => {
                    event.preventDefault();
                    if (!disabled) {
                        handleSavePanel();
                    }
                }, placeholder: _T('common.name') })),
        React.createElement(Space, { style: { width: '100%', justifyContent: 'flex-end', marginTop: 12 } },
            React.createElement(Button, { type: "primary", onClick: openCreatePanel, disabled: disabled },
                React.createElement(PlusOutlined, null),
                _T('common.add')))));
};
